import { useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Card, EmptyState, Pill, Avatar } from "../components/primitives";
import { PanelRow } from "../components/cockpit/Panel";
import { NewTaskModal } from "../components/scope/NewTaskModal";
import { TaskEditModal } from "../components/scope/TaskEditModal";
import { getAgentPersona } from "../components/cockpit/CockpitHeader";
import { useListMyTasks } from "../hooks/use-list-my-tasks";
import { useCompleteTask, useUpdateTask } from "../hooks/use-task-mutations";
import { useWorkspaceInfo } from "../../lib/hooks/use-kuvio";
import { useChatScope } from "../hooks/use-chat-scope";
import type { Task, TaskStatus, WorkspaceTask } from "../../lib/types";

const FILTERS: { key: TaskStatus; label: string }[] = [
  { key: "inbox", label: "Inbox" },
  { key: "queued", label: "Queued" },
  { key: "done", label: "Done" },
  { key: "dropped", label: "Dropped" },
];
const OPEN_STATUSES: TaskStatus[] = ["inbox", "queued"];

/** Map a cross-agent WorkspaceTask onto the Task shape the editor expects. */
function toTask(t: WorkspaceTask): Task {
  return {
    id: t.id,
    title: t.title,
    body: t.body ?? undefined,
    status: t.status as TaskStatus,
    due_by: t.due_by ?? undefined,
    created_at: t.created_at,
    updated_at: t.updated_at,
    agent_id: t.agent_id,
    agent_name: t.agent_name,
    agent_path: t.agent_path,
  };
}

/**
 * "My tasks" (Sprint 26.0) — every open task across the workspace, owned by the
 * one task noun. The daily "what should I do next" landing. Sprint 26.0 replaced
 * per-user assignment + task grouping with the cross-agent task roll-up.
 */
export function MyTasksPage() {
  const navigate = useNavigate();
  // Explicit workspace scope (was a fall-through).
  useChatScope({ kind: "workspace", label: "My tasks" }, null, []);
  const list = useListMyTasks({ includeDone: true });
  const workspace = useWorkspaceInfo();
  const completeTask = useCompleteTask();
  const updateTask = useUpdateTask();
  const [newOpen, setNewOpen] = useState(false);
  const [editTask, setEditTask] = useState<WorkspaceTask | null>(null);
  const [show, setShow] = useState<Set<TaskStatus>>(() => new Set(OPEN_STATUSES));

  const allTasks = useMemo(() => list.data?.tasks ?? [], [list.data]);
  const visible = useMemo(
    () => allTasks.filter((t) => show.has(t.status as TaskStatus)),
    [allTasks, show],
  );
  const openCount = useMemo(
    () => allTasks.filter((t) => t.status === "inbox" || t.status === "queued").length,
    [allTasks],
  );

  function toggleFilter(key: TaskStatus) {
    setShow((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleComplete(t: WorkspaceTask) {
    if (t.status === "done") {
      updateTask.mutate({ node_id: t.agent_id, task_id: t.id, status: "queued" });
    } else {
      completeTask.mutate({ node_id: t.agent_id, task_id: t.id });
    }
  }

  const isLoading = list.isLoading;
  const rootNodeId = workspace.data?.root_node_id ?? undefined;

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>My tasks</h1>
          {openCount > 0 && <Pill variant="blue">{openCount} open</Pill>}
        </div>
        <button
          data-variant="primary"
          onClick={() => setNewOpen(true)}
          disabled={!rootNodeId}
          style={{ fontSize: 13, padding: "8px 14px", display: "flex", alignItems: "center", gap: 6 }}
        >
          <i className="ti ti-plus" style={{ fontSize: 14 }} aria-hidden="true" /> New task
        </button>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {FILTERS.map((f) => {
          const on = show.has(f.key);
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => toggleFilter(f.key)}
              style={filterPill(on)}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <Card>
          <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading your tasks...</span>
          </div>
        </Card>
      ) : list.error ? (
        <Card>
          <EmptyState>Couldn't load your tasks.</EmptyState>
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <button onClick={() => list.refetch()} style={{ fontSize: 13, color: "var(--color-text-info)", background: "none", border: "none", cursor: "pointer" }}>
              Retry
            </button>
          </div>
        </Card>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState>Nothing open. New tasks across the workforce show up here.</EmptyState>
        </Card>
      ) : (
        <Card>
          {visible.map((t) => (
            <MyTaskRow
              key={t.id}
              task={t}
              onToggleComplete={() => toggleComplete(t)}
              onOpen={() => navigate(`/tasks/${t.id}`)}
              onEdit={() => setEditTask(t)}
            />
          ))}
        </Card>
      )}

      {rootNodeId && <NewTaskModal open={newOpen} onClose={() => setNewOpen(false)} nodeId={rootNodeId} />}
      {editTask && (
        <TaskEditModal
          open={!!editTask}
          onClose={() => setEditTask(null)}
          task={toTask(editTask)}
          onDeleted={() => setEditTask(null)}
        />
      )}
    </div>
  );
}

function MyTaskRow({
  task, onToggleComplete, onOpen, onEdit,
}: {
  task: WorkspaceTask;
  onToggleComplete: () => void;
  onOpen: () => void;
  onEdit: () => void;
}) {
  const done = task.status === "done";
  const dropped = task.status === "dropped";
  const due = task.due_by;
  const overdue = !!due && !done && !dropped && new Date(due) < new Date(new Date().toDateString());

  return (
    <PanelRow onClick={onOpen} title={task.title}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleComplete(); }}
        aria-label={done ? "Mark incomplete" : "Mark done"}
        title={done ? "Mark incomplete" : "Mark done"}
        style={completeToggle(done)}
      >
        {done && <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" />}
      </button>
      <span style={titleStyle(done || dropped)}>{task.title}</span>
      <Avatar persona={getAgentPersona({ id: task.agent_id, path: task.agent_path })} name={task.agent_name} size="sm" />
      <span style={hostStyle}>{task.agent_name}</span>
      {due && (
        <span style={{ ...dueStyle, color: overdue ? "var(--c-red-600)" : "var(--color-text-tertiary)" }} title={`Due ${due}`}>
          <i className="ti ti-calendar-event" style={{ fontSize: 12 }} aria-hidden="true" /> {formatDue(due)}
        </span>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        aria-label="Edit task"
        title="Edit task"
        style={editBtn}
      >
        <i className="ti ti-pencil" style={{ fontSize: 13 }} aria-hidden="true" />
      </button>
    </PanelRow>
  );
}

function formatDue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 16,
};

const titleStyle = (muted: boolean): CSSProperties => ({
  fontSize: 13,
  fontWeight: 500,
  color: muted ? "var(--color-text-tertiary)" : "var(--color-text-primary)",
  textDecoration: muted ? "line-through" : "none",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  flex: 1,
});

const hostStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};

const dueStyle: CSSProperties = {
  fontSize: 11,
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  whiteSpace: "nowrap",
};

const editBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 2,
  display: "inline-flex",
  flexShrink: 0,
};

const completeToggle = (done: boolean): CSSProperties => ({
  width: 18,
  height: 18,
  flexShrink: 0,
  borderRadius: "50%",
  border: done ? "1px solid var(--c-teal-600)" : "1px solid var(--color-border-secondary)",
  background: done ? "var(--c-teal-600)" : "transparent",
  color: "#fff",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
});

function filterPill(on: boolean): CSSProperties {
  return {
    fontSize: 12,
    padding: "4px 12px",
    borderRadius: 999,
    border: on ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
    background: on ? "var(--c-blue-50)" : "var(--color-background-primary)",
    color: on ? "var(--color-text-info)" : "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: on ? 500 : 400,
  };
}
